import { useEffect } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Link, useLocation } from 'react-router-dom';
import {
  QuestionMarkCircleIcon,
  BookOpenIcon,
  SparklesIcon,
  RocketLaunchIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import config from '@/config';
import { useFlag } from '@/hooks/useFeatureFlags';
import { readStoredThemePreference } from '@/hooks/useUIPreferences';
import { trackEvent, currentLocationTag, toEventSlug } from '@/lib/analytics';
import type { FlagName } from '@/types/feature-flags.generated';

/**
 * Help entry in the top navigation (task 538, report Pillar 2).
 *
 * Deliberately *not* in the avatar menu: help is a primary affordance, and
 * burying it is the state this change removes. Four entries — the
 * documentation website, the "What's an AI Service?" explainer, the live
 * examples, and contact support (an in-app form since task 607, a mailto
 * before it) — plus the help assistant panel (task 545), which carries its own
 * flag.
 *
 * Two of those entries open an overlay rather than navigating: the help
 * assistant panel and, since task 605, the explainer dialog. Both overlays are
 * owned by `AppLayout`, because the Menu unmounts its items on close and an
 * overlay has to outlive that.
 *
 * Every docs link carries `?source=app-help` so the docs site's readership
 * analytics can split "reached from inside the product" from email and
 * organic traffic (report Pillar 3).
 */

/** One flag governs the whole menu. */
export const HELP_MENU_FLAG = 'innovate.fe.help.menu';

/**
 * The help assistant entry carries its own flag on top of the menu's (task
 * 545), so the chat panel can be rolled out — or pulled — without taking the
 * documentation and support links with it.
 */
export const HELP_ASSISTANT_FLAG = 'innovate.fe.help.assistant';

/** Attribution parameter carried by every outbound documentation link. */
export const HELP_LINK_SOURCE = 'app-help';

/**
 * Appends the in-app attribution parameter to a documentation URL, plus the
 * reader's theme when they have actually chosen one. The base comes from
 * `config.DOCS_URL` (`VITE_DOCS_URL`) — never hardcoded, and never defaulted,
 * so a missing variable is visible instead of silently wrong.
 *
 * **`system` deliberately carries nothing** (task 767). The app and the docs
 * site are different origins, so the theme has to travel on the link — but a
 * User on `system` has expressed no preference to carry, and the docs site's
 * own default already tracks the same OS setting the app is tracking. Sending a
 * resolved `light`/`dark` for them would *pin* the docs site to whatever the OS
 * said at click time, breaking the very thing `system` asked for.
 */
export function docsLink(base: string = config.DOCS_URL): string {
  const separator = base.includes('?') ? '&' : '?';
  const theme = readStoredThemePreference();
  const themeParam = theme === 'light' || theme === 'dark' ? `&theme=${theme}` : '';
  return `${base}${separator}source=${HELP_LINK_SOURCE}${themeParam}`;
}

/**
 * Link to a single published guide by its docs-site slug, carrying the same
 * attribution parameter. The help assistant cites guides by slug rather than
 * URL (task 544) precisely so the base stays a config read here.
 */
export function docsGuideLink(slug: string): string {
  const base = config.DOCS_URL.replace(/\/$/, '');
  return docsLink(`${base}/${slug}/`);
}

type HelpEntryKind = 'external' | 'internal' | 'mailto' | 'panel';

interface HelpEntry {
  key: string;
  label: string;
  kind: HelpEntryKind;
  /** Resolved at render time so config reads stay lazy. */
  href: () => string;
  icon: typeof BookOpenIcon;
  /**
   * Reserved slots stay in the structure but are not rendered — the task that
   * owns the slot flips this on when its surface exists.
   */
  rendered: boolean;
  /** Optional second gate, on top of the menu's own flag. */
  flag?: FlagName;
}

const HELP_ENTRIES: HelpEntry[] = [
  {
    key: 'documentation',
    label: 'Documentation',
    kind: 'external',
    href: () => docsLink(),
    icon: BookOpenIcon,
    rendered: true,
  },
  {
    // The "What's an AI Service?" explainer (task 605). It used to deep-link to
    // an anchor inside the first-run Dashboard checklist, which only exists
    // while that checklist is on screen; it now opens a dialog owned by
    // `AppLayout`, so the explainer is reachable from any page at any account
    // state. Event contract unchanged (task 541): `help_link_clicked` with
    // `destination: 'whats_an_ai_service'`.
    key: 'whats-an-ai-service',
    label: "What's an AI Service?",
    kind: 'panel',
    href: () => '',
    icon: SparklesIcon,
    rendered: true,
  },
  {
    // "See working examples" (task 611). Was a direct hop to the widget demo's
    // public site (`LIVE_EXAMPLES.widget.url`), which landed the User on what
    // reads as an unrelated marketing page with no framing. It now points at
    // the Examples overview guide on the docs site (published by task 610),
    // which introduces the three live demos before sending the User onward.
    // Event contract unchanged (task 541): `help_link_clicked` with
    // `destination: 'working_examples'`.
    key: 'working-examples',
    label: 'See working examples',
    kind: 'external',
    href: () => docsGuideLink('innovate-examples-overview'),
    icon: RocketLaunchIcon,
    rendered: true,
  },
  {
    // Contact support (task 607). Was a mailto to the monitored inbox; it now
    // navigates to the in-app `/support` form, which posts to the intake
    // Lambda with the caller's own token — so a ticket arrives with verified
    // identity attached instead of whatever address the User's mail client
    // happened to be signed in as. The internal render branch carries the
    // originating path as router state, giving support the page context for
    // free. Event contract unchanged (task 541): `help_link_clicked` with
    // `destination: 'contact_support'`.
    key: 'contact-support',
    label: 'Contact support',
    kind: 'internal',
    href: () => '/support',
    icon: EnvelopeIcon,
    rendered: true,
  },
  {
    // The help assistant chat panel (task 545). It opens a panel rather than
    // navigating, so it carries no href. Event contract (task 541): opening it
    // emits `help_link_clicked` with `destination: 'help_assistant'` from this
    // menu, and the panel itself emits `help_assistant_asked` on each submitted
    // question — `{ location, turn }` through `trackEvent`, never the question
    // text.
    key: 'help-assistant',
    // The User asks INNOVATE, not a named helper inside it (task 750). The key,
    // the flag and the analytics destination stay `help_assistant` — renaming
    // those would break the event contract for a copy change.
    label: 'Ask Innovate',
    kind: 'panel',
    href: () => '',
    icon: ChatBubbleLeftRightIcon,
    rendered: true,
    flag: HELP_ASSISTANT_FLAG,
  },
];

const ITEM_CLASSES =
  'flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 transition-colors data-[focus]:bg-gray-50 dark:data-[focus]:bg-gray-700';

/**
 * `help_opened` (task 541), emitted once per opening of the menu — mouse or
 * keyboard, since the Menu's own `open` state is the trigger rather than a
 * click handler on the button. Rendered inside the Menu render prop, which is
 * why it is a component: the `open` transition needs an effect.
 */
function HelpMenuOpenEvent({ open }: { open: boolean }) {
  useEffect(() => {
    if (!open) return;
    trackEvent('help_opened', { location: currentLocationTag() });
  }, [open]);
  return null;
}

/**
 * `help_link_clicked` (task 541). `destination` is the entry key as a stable
 * snake_case slug — the reserved help-assistant slot picks its own up for free
 * when task 545 renders it. Exported so in-page guide links (the Testing tab's,
 * task 1193) emit the same event with their own destination. `location`
 * defaults to the route tag; a link whose placement matters more than its route
 * names the placement (the Testing rail's ?, task 1195).
 */
export function trackHelpLinkClicked(entryKey: string, location: string = currentLocationTag()): void {
  trackEvent('help_link_clicked', {
    destination: toEventSlug(entryKey),
    location,
  });
}

interface HelpMenuProps {
  /**
   * Opens the help assistant panel (task 545). The panel is owned by
   * `AppLayout`, not by this menu: the Menu unmounts its items on close, and
   * the panel has to outlive that.
   */
  onOpenAssistant?: () => void;
  /**
   * Opens the "What's an AI Service?" explainer dialog (task 605). Owned by
   * `AppLayout` for the same reason as the assistant panel.
   */
  onOpenWhatIsAnAiService?: () => void;
}

export default function HelpMenu({
  onOpenAssistant,
  onOpenWhatIsAnAiService,
}: HelpMenuProps = {}) {
  const helpEnabled = useFlag(HELP_MENU_FLAG);
  const assistantEnabled = useFlag(HELP_ASSISTANT_FLAG);
  // Where the User was when they opened help — handed to internal
  // destinations as router state so `/support` can offer the originating page
  // as context (task 607) instead of asking the User to describe it.
  const location = useLocation();

  if (!helpEnabled) return null;

  const flagState: Record<string, boolean> = {
    [HELP_ASSISTANT_FLAG]: assistantEnabled,
  };

  const entries = HELP_ENTRIES.filter(
    (entry) => entry.rendered && (!entry.flag || flagState[entry.flag] === true)
  );

  /**
   * Panel entries open an overlay `AppLayout` owns instead of navigating; each
   * one names its opener here, so adding a panel entry never touches the
   * rendering below.
   */
  const panelOpeners: Record<string, (() => void) | undefined> = {
    'help-assistant': onOpenAssistant,
    'whats-an-ai-service': onOpenWhatIsAnAiService,
  };

  return (
    <Menu as="div" className="relative flex">
      {({ open }) => (
        <>
          <HelpMenuOpenEvent open={open} />
          <MenuButton
            data-testid="nav-help-menu"
            aria-label="Help"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <QuestionMarkCircleIcon className="h-5 w-5" />
            <span className="hidden sm:inline">Help</span>
          </MenuButton>
          <MenuItems
            data-testid="nav-help-menu-panel"
            className="absolute right-0 top-full z-50 mt-2 w-60 origin-top-right rounded-lg border border-gray-200 bg-white py-1 shadow-lg focus:outline-none dark:border-gray-700 dark:bg-gray-800"
          >
            {entries.map((entry) => {
              const Icon = entry.icon;
              const testId = `help-menu-${entry.key}`;
              return (
                <MenuItem key={entry.key}>
                  {entry.kind === 'panel' ? (
                    <button
                      type="button"
                      data-testid={testId}
                      className={`${ITEM_CLASSES} w-full text-left`}
                      onClick={() => {
                        trackHelpLinkClicked(entry.key);
                        panelOpeners[entry.key]?.();
                      }}
                    >
                      <Icon className="h-4 w-4 text-gray-400" />
                      {entry.label}
                    </button>
                  ) : entry.kind === 'internal' ? (
                    <Link
                      to={entry.href()}
                      state={{ from: location.pathname }}
                      data-testid={testId}
                      className={ITEM_CLASSES}
                      onClick={() => trackHelpLinkClicked(entry.key)}
                    >
                      <Icon className="h-4 w-4 text-gray-400" />
                      {entry.label}
                    </Link>
                  ) : (
                    <a
                      href={entry.href()}
                      data-testid={testId}
                      className={ITEM_CLASSES}
                      onClick={() => trackHelpLinkClicked(entry.key)}
                      {...(entry.kind === 'external'
                        ? { target: '_blank', rel: 'noopener noreferrer' }
                        : {})}
                    >
                      <Icon className="h-4 w-4 text-gray-400" />
                      {entry.label}
                    </a>
                  )}
                </MenuItem>
              );
            })}
          </MenuItems>
        </>
      )}
    </Menu>
  );
}
